import {gql, useMutation, useQuery} from '@apollo/client';
import {Box, Button, Link, Tooltip, Typography} from '@mui/material';
import {useSnackbar} from 'notistack';
import React, {PropsWithChildren, useMemo, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {
  GridCellParams,
  GridColDef,
  GridRowParams,
  GridSortModel,
} from '@mui/x-data-grid-pro';
import {HistoryOutlined} from '@mui/icons-material';
import Loading from '../../components/Loading/Loading';
import {Panel} from '../../components/Panel/Panel';
import {useAccountContext} from '../../contexts/AccountContext';
import {ReviewContextProvider, useReviewContext} from '../../contexts/ReviewContext';
import {useSubmitHandover} from '../../hooks/useSubmitHandover';
import {useReviewDataValidation} from '../../hooks/useClientDataValidation';
import {KeyClientDataBanner} from '../../components/KeyClientDataBanner/KeyClientDataBanner';
import {themeColors} from '../../theme/Theme';
import EditorGrid from '../../components/EditorGrid/EditorGrid';
import {recommendsGridStyles} from '../../features/recommendations/RecommendsGrid/styles';
import {ShowIfDebugWrapper, useDebugContext} from '../../contexts/DebugProvider';
import {onNewRecommendFactory} from '../../features/recommendations/ClientRecommendsPage/ClientRecommends';
import {REVIEW_DETAILS_FRAGMENT} from '../../graphql/fragments';
import {GetSppReview, GetSppReviewVariables} from '../../schema-types/GetSppReview';
import {
  CreateSppReview,
  CreateSppReviewVariables,
} from '../../schema-types/CreateSppReview';
import {useLocalStorage} from '../../hooks/useLocalStorage';
import {useAccountOVRecommends} from '../../hooks/useOVRecommends';
import {FeatureBoundary, hasPermission} from '../../contexts/FeatureBoundary';
import {sppDAColumns} from '../../features/recommendations/RecommendsGrid/sppRecommendsColumns';
import {
  GlobalPermissionEnum,
  OVModelsEnum,
  OVRecommendActionEnum,
} from '../../schema-types/globalTypes';
import HistoryDiffDialog from '../../components/AccountReviewDialog/HistoryDiffDialog';
import {HistoryQueryType} from '../../components/AccountReviewDialog/useHistoryDiff';
import {SPPDetailPanel} from './SPPDetailPanel';
import {getEnvironment} from '../../utils/envTest';
import {useAccountReviews} from '../../hooks/useAccountReviews';
import {ScrollableModal} from '../../components/Common/ScrollableModal';
import {OVRecommendation} from '../../schema-types/OVRecommendation';
import useDjango from '../../hooks/useDjango';
import validateUUID from '../../utils/validateUUID';
import {useRequirementsDentistOrBAU} from '../../hooks/useRequirementsDentistOrBAU';

export const GET_SPP_REVIEW = gql`
  query GetSppReview($accountId: UUID!) {
    sppReview(accountId: $accountId) {
      __typename
      ... on ReviewType {
        ...ReviewDetailsFragment
      }
      ... on SppReviewErrorType {
        errorMessage
      }
    }
  }
  ${REVIEW_DETAILS_FRAGMENT}
`;

export const CREATE_SPP_REVIEW = gql`
  mutation CreateSppReview($accountId: UUID!, $ignoreLimit: Boolean) {
    createSppReview(accountId: $accountId, ignoreLimit: $ignoreLimit) {
      ok
      errors
      review {
        ...ReviewDetailsFragment
      }
    }
  }
  ${REVIEW_DETAILS_FRAGMENT}
`;

export const useGetSppReview = (accountId: string, options: {skip?: boolean} = {}) => {
  const {data, loading, error, refetch} = useQuery<GetSppReview, GetSppReviewVariables>(
    GET_SPP_REVIEW,
    {
      variables: {accountId: validateUUID(accountId)!},
      skip: options.skip,
    }
  );
  const review = data?.sppReview?.__typename === 'ReviewType' ? data.sppReview : null;
  const errors =
    data?.sppReview?.__typename === 'SppReviewErrorType'
      ? data.sppReview.errorMessage
      : null;
  return {
    reviewId: review?.id,
    loading,
    error: errors?.join('. ') || error?.message,
    review,
    refetch,
  };
};

export const useLazyGetSppReview = () => {
  const {account, accountId} = useAccountContext();
  const {data, loading, error, called} = useQuery<GetSppReview, GetSppReviewVariables>(
    GET_SPP_REVIEW,
    {
      variables: {
        accountId: account?.id ? validateUUID(account.id)! : validateUUID(accountId)!,
      },
    }
  );
  const review = data?.sppReview?.__typename === 'ReviewType' ? data.sppReview : null;
  const errors =
    data?.sppReview?.__typename === 'SppReviewErrorType'
      ? data.sppReview.errorMessage
      : null;
  return {
    reviewId: review?.id,
    loading,
    error: errors?.join('. ') || error?.message,
    review,

    called,
  };
};

export const useEnsureSppReview = (accountId: string) => {
  const {reviewId, error, loading, refetch} = useGetSppReview(accountId);
  const ignoreLimit = getEnvironment() !== 'PROD';
  const [createSppReview, {loading: creating, error: createError, data: createData}] =
    useMutation<CreateSppReview, CreateSppReviewVariables>(CREATE_SPP_REVIEW, {
      variables: {accountId: validateUUID(accountId)!, ignoreLimit},
      onCompleted: () => {
        refetch();
      },
    });
  const [hasTriedCreate, setHasTriedCreate] = React.useState(false);

  React.useEffect(() => {
    if (loading || creating || reviewId || error || hasTriedCreate) return;
    setHasTriedCreate(true);
    createSppReview();
  }, [loading, creating, reviewId, error, hasTriedCreate, createSppReview]);

  const mutationErrors = createData?.createSppReview?.errors?.join('. ') ?? null;
  const combinedError = error || createError?.message || mutationErrors || undefined;
  return {
    reviewId,
    loading: loading || creating,
    error: combinedError,
  };
};

export const InstructionsPage: React.FC = () => {
  const {account, accountId} = useAccountContext();
  const {reviewId, error, loading} = useEnsureSppReview(accountId!);

  const [saving, setSaving] = useState(false);
  if (saving) {
    return (
      <Panel color={themeColors.lightGreen} title="Submitting instructions">
        <Box
          display="flex"
          justifyContent="center"
          flexDirection="column"
          alignContent="center"
          padding={2}
        >
          <Loading />
          <Box mt={2} />
          <Typography align="center" variant="body1">
            This may take a few moments
          </Typography>
        </Box>
      </Panel>
    );
  }

  if (loading) {
    return <Loading entityName="instructions" />;
  }

  if (error) {
    return (
      <Panel title="Error">
        <Box>
          <Typography variant="h5">Unable to continue</Typography>
          <Typography variant="body1">Error: {error || 'No id'}</Typography>
        </Box>
        <Button onClick={() => window.location.reload()}>Try again</Button>
      </Panel>
    );
  }

  if (!reviewId) {
    return (
      <Panel title="Error">
        <Box>
          <Typography variant="h5">Create new set of instructions</Typography>
        </Box>
        <Button onClick={() => window.location.reload()}>
          Click to create set of instructions
        </Button>
      </Panel>
    );
  }

  return (
    <ReviewContextProvider forceReviewId={reviewId!}>
      <AccountActionsPageInner saving={saving} setSaving={setSaving} />
    </ReviewContextProvider>
  );
};

export const calculateAllFinalised = (reccs: any[]) => {
  return (
    !!reccs &&
    reccs.length > 0 &&
    reccs.length === reccs.filter((x) => x.isFinalised).length
  );
};

export const AccountActionsPageInner: React.FC<{
  setSaving: React.Dispatch<React.SetStateAction<boolean>>;
  saving: boolean;
}> = ({saving, setSaving}) => {
  const {onSPPDASubmit} = useSubmitHandover();
  const {closeSnackbar, enqueueSnackbar} = useSnackbar();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const {reviewId, allValid, reviewDetails} = useReviewContext();
  const {reviewDataIssues, hasReviewDataIssues} = useReviewDataValidation();
  const canFullSubmit = allValid && !hasReviewDataIssues;

  const stepValidationString = useMemo(() => {
    return Object.values(reviewDetails?.validation ?? {})
      .flat()
      .filter((v): v is string => v !== null)
      .join(', ');
  }, [reviewDetails?.validation]);

  const [showWarning, setShowWarning] = useState(false);
  const {data} = useAccountOVRecommends();
  const allFinalised = calculateAllFinalised(data?.ovrecommends || []);

  const handleStartSave = () => {
    if (isSubmitting) {
      return;
    }
    setShowWarning(true);
  };
  const handleSave = () => {
    if (isSubmitting) {
      return;
    }
    setSaving(true);
    setIsSubmitting(true);

    closeSnackbar();
    // eslint-disable-next-line consistent-return
    return onSPPDASubmit({
      variables: {
        reviewId: validateUUID(reviewId)!,
      },
    })
      .then(async (res) => {
        if (res.data?.submitHandover?.ok) {
          enqueueSnackbar('Instructions submitted', {
            variant: 'success',
          });

          setTimeout(() => {
            window.location.reload();
          }, 2000);
        } else {
          enqueueSnackbar(
            <>
              <Box>
                URL: {window.location.href}
                <Typography>Errors:</Typography>
                {res.data?.submitHandover?.errors?.map((x, i) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <Typography key={i}>
                    Error #{i + 1}: {x}
                  </Typography>
                ))}
                <Typography mt={3}>
                  Please
                  <Link
                    href="https://saltus.freshdesk.com/support/tickets/new"
                    target="_blank"
                    rel="noreferrer"
                    color="#fff"
                  >
                    click here
                  </Link>
                  to open a support ticket, copying and pasting the error message above.
                </Typography>
              </Box>
            </>,
            {
              variant: 'error',
              autoHideDuration: null,
            }
          );
        }
      })
      .catch(() => {
        enqueueSnackbar('Failed to submit handover', {
          variant: 'error',
          autoHideDuration: null,
        });
      })
      .finally(() => {
        setIsSubmitting(false);
        setShowWarning(false);
      });
  };

  return (
    <>
      <InstructionsGrid key={reviewId}>
        <ScrollableModal
          size="large"
          open={showWarning}
          onClose={() => {
            setShowWarning(false);
          }}
        >
          <Panel title="">
            <Box p={2} pt={4}>
              <KeyClientDataBanner issues={reviewDataIssues} isSFP={false} />
              <Typography variant="h4">
                Please include <u>all</u> instructions before submitting.
              </Typography>
              <Box mt={4} />
              <Box display="flex">
                <Box m={1} padding={0.5}>
                  If submitting a Pension, further information will be required. Please
                  submit here
                </Box>
                <Button
                  href="https://saltuspartnersllp.freshservice.com/support/home"
                  target="_blank"
                  color="success"
                  variant="contained"
                >
                  Pension Info
                </Button>
              </Box>
              <Box mt={4} />
              <Typography variant="h6">
                If you have created a new plan, you need to include <strong>all</strong>{' '}
                associated instructions. That includes contributions, transfers and
                withdrawals.
              </Typography>
              <Box mt={4} />
              <Typography variant="h6">
                Once you submit an instruction for a new plan it needs to be set up and
                finalised by the Saltus Operations team. During this time, the new plan
                will not be available for further instructions.
              </Typography>

              <Box
                width="100%"
                mt={8}
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={() => setShowWarning(false)}
                >
                  Cancel
                </Button>
                <Tooltip
                  title={
                    stepValidationString !== 'ReviewValidationType'
                      ? stepValidationString
                      : undefined
                  }
                >
                  <span>
                    {/* the span above is a hack to show the tooltip even on disabled buttons */}
                    <Button
                      variant="outlined"
                      color="primary"
                      onClick={handleSave}
                      disabled={isSubmitting || !allFinalised || !canFullSubmit}
                    >
                      {isSubmitting ? 'Submitting...' : 'Submit instructions'}
                    </Button>
                  </span>
                </Tooltip>
              </Box>
            </Box>
          </Panel>
        </ScrollableModal>
        <Box display="flex" alignItems="center" justifyContent="flex-end">
          {allFinalised ? null : <Typography>Drafts prevent submission</Typography>}
          <Box ml={2} />
          <Button
            variant="outlined"
            color="primary"
            onClick={handleStartSave}
            disabled={isSubmitting || !allFinalised}
          >
            {isSubmitting ? 'Submitting...' : 'Submit instructions'}
          </Button>
        </Box>
      </InstructionsGrid>
    </>
  );
};

export const InstructionsGrid: React.FC<PropsWithChildren<{}>> = ({children}) => {
  const {djangoUser} = useDjango();
  const {accountId} = useAccountContext();
  const {debug} = useDebugContext();
  const {accountReviewsLoading} = useAccountReviews({
    variables: {
      accountId: validateUUID(accountId)!,
      idType: 'id',
      curoSync: true,
      includeProgressSync: true,
    },
  });
  const {data, error, loading} = useAccountOVRecommends();
  const navigate = useNavigate();
  const [filters, setFilters] = useLocalStorage<GridSortModel>('instructionsGrid202307', [
    {field: 'dateCreated', sort: 'desc'},
  ]);
  const onNewRecommend = onNewRecommendFactory({navigate});
  const {reviewId} = useReviewContext();
  const classesRex = recommendsGridStyles();
  const recommends = data?.ovrecommends || [];
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedReviewId, setSelectedReviewId] = useState<string | null>(null);
  const columns: GridColDef[] = debug?.forms
    ? [
        {
          field: 'ID',
          valueGetter: (params, row) => {
            return row.id;
          },
        },
        ...sppDAColumns,
      ]
    : sppDAColumns;

  const handleDialogOpen = (id: string) => {
    setSelectedReviewId(id);
    setDialogOpen(true);
  };
  const handleDialogClose = () => {
    setSelectedReviewId('');
    setDialogOpen(false);
  };

  const recommendsHistoryColumn = {
    field: 'history',
    headerName: 'History',
    flex: 1,
    editable: false,
    renderCell: (params: GridCellParams) => {
      return (
        <HistoryOutlined
          style={{cursor: 'pointer'}}
          data-testid="history-review"
          onClick={() => handleDialogOpen(params.id.toString())}
        />
      );
    },
  };

  const columnsWithHistory = [
    ...columns.slice(0, -1),
    ...(hasPermission(djangoUser, [GlobalPermissionEnum.can_access_admin_features])
      ? [recommendsHistoryColumn]
      : []),
    ...columns.slice(-1),
  ];

  const {createRecommendUrl} = useRequirementsDentistOrBAU();

  return (
    <FeatureBoundary
      features={[GlobalPermissionEnum.can_access_ehons_reqs]}
      showErrorIfDenied
    >
      <div
        style={{
          height: '100%',
          flexGrow: 1,
          position: 'relative',
        }}
      >
        <Panel color={themeColors.yellow} title="Instructions">
          <ShowIfDebugWrapper debugKey="forms">Review id: {reviewId}</ShowIfDebugWrapper>
          <Box mb={1} display="flex" justifyContent="space-between" mt={1}>
            <Button variant="contained" color="primary" onClick={onNewRecommend}>
              New instruction
            </Button>
            {children}
          </Box>
          <Box mt={2} />
          <EditorGrid
            dataGridId="InstructionsGrid"
            sx={classesRex.dataGridRoot}
            onCellDoubleClick={(params) => {
              // maybe this will fix it for paul
              navigate({
                pathname: `../${createRecommendUrl}`,
                search: `ovrecommendId=${params.id}`,
              });
            }}
            columns={columnsWithHistory}
            rows={recommends}
            hideFooter
            error={error}
            sortModel={filters}
            onSortModelChange={setFilters}
            loading={loading}
            minHeight="600px"
            getDetailPanelHeight={({row}) => 'auto'}
            getDetailPanelContent={({row}: GridRowParams<OVRecommendation>) => {
              if (row.action === OVRecommendActionEnum.AD_HOC_FEE) {
                return null;
              }
              return (
                <SPPDetailPanel loading={accountReviewsLoading || loading} row={row} />
              );
            }}
            createFunc={false}
            createFuncLoading={false}
            syncFunc={() => {}}
            updateFunc={false}
          />
        </Panel>
      </div>
      <HistoryDiffDialog
        isOpen={dialogOpen}
        handleClose={handleDialogClose}
        entityId={selectedReviewId}
        entityType={OVModelsEnum.OVRECOMMEND}
        queryType={HistoryQueryType.GENERIC}
      />
    </FeatureBoundary>
  );
};
